import { z } from 'zod';
import { objectToFormData } from '@/lib/utils';
import apiSlice from '../api/api.slice';
import {
  AuthResponse,
  IUser,
  LoginRequest,
  PasswordChangeRequest,
  IUserSignupData,
  ICompleteUserSignup,
} from './auth';
import { editProfileFormSchema, forgetPasswordSchema } from './auth.schema';
import { CNCModelI } from '../cncModel/cncModel';

const authApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: ({ email, password }) => ({
        url: '/api/v1/auth/login',
        method: 'POST',
        body: { email, password },
      }),
      transformResponse: (response: AuthResponse) => response,
    }),
    signup: builder.mutation<AuthResponse, IUserSignupData>({
      query: (body) => ({
        url: '/api/v1/auth/register',
        method: 'POST',
        body: objectToFormData(body),
      }),
      transformResponse: (response: AuthResponse) => response,
    }),
    getUser: builder.query<IUser, void>({
      query: () => ({
        url: '/api/v1/auth/me',
        method: 'GET',
      }),
      transformResponse: (response: { user: IUser }) => {
        return response.user;
      },
    }),
    getSignupSession: builder.query<IUser, void>({
      query: () => ({
        url: '/api/v1/auth/signup-session',
        method: 'GET',
      }),
      transformResponse: (response: { user: IUser }) => response.user,
    }),
    checkUsernameAvailability: builder.query<boolean, string>({
      query: (username) => ({
        url: `/api/v1/auth/usernameAvailability`,
        method: 'GET',
        params: {
          username,
        },
      }),
      transformResponse: (response: { available: boolean }) =>
        response.available,
    }),
    updateUserData: builder.mutation<
      AuthResponse,
      z.infer<typeof editProfileFormSchema>
    >({
      query: (body) => ({
        url: '/api/v1/auth/updateUser',
        method: 'POST',
        body: objectToFormData(body),
      }),
    }),
    updateUserPassword: builder.mutation<AuthResponse, PasswordChangeRequest>({
      query: (body) => ({
        url: '/api/v1/auth/updatePassword',
        method: 'PATCH',
        body,
      }),
    }),
    logout: builder.mutation<AuthResponse, void>({
      query: () => ({
        url: '/api/v1/auth/logout',
        method: 'GET',
      }),
    }),
    getGoogleSignInLink: builder.query<{ url: string }, void>({
      query: () => ({
        url: '/api/v1/auth/generateGoogleSignInLink',
        method: 'GET',
      }),
      transformResponse: (response: { url: string }) => response,
    }),
    getVerificationCode: builder.query<string, void>({
      query: () => ({
        url: '/api/v1/auth/getVerificationOtp',
        method: 'GET',
      }),
      transformResponse: (response: string) => response,
    }),
    validateVerificationCode: builder.mutation<AuthResponse, string>({
      query: (otp) => ({
        url: '/api/v1/auth/validateOtp',
        method: 'POST',
        body: {
          otp,
        },
      }),
      transformResponse: (response: AuthResponse) => response,
    }),
    completeUserSignup: builder.mutation<
      {
        status: string;
        user: IUser;
      },
      ICompleteUserSignup
    >({
      query: (userData) => ({
        url: '/api/v1/auth/completeUserSignup',
        method: 'POST',
        body: objectToFormData(userData),
      }),
    }),
    sendForgetPasswordEmail: builder.mutation<
      {
        status: string;
        message: string;
      },
      z.infer<typeof forgetPasswordSchema>
    >({
      query: (body) => ({
        url: '/api/v1/auth/sendPasswordResetEmail',
        method: 'POST',
        body,
      }),
    }),
    resetPassword: builder.mutation<
      {
        status: string;
        message: string;
      },
      {
        password: string;
        token: string;
      }
    >({
      query: (body) => ({
        url: '/api/v1/auth/resetPassword',
        method: 'POST',
        body,
      }),
    }),
    changePassword: builder.mutation<
      {
        status: string;
        message: string;
      },
      {
        newPassword: string;
        currentPassword: string;
      }
    >({
      query: (body) => ({
        url: '/api/v1/auth/updateUserPassword',
        method: 'PATCH',
        body,
      }),
    }),
    getUserByUsername: builder.query<{ cncModel: CNCModelI[] } & IUser, string>(
      {
        query: (username) => ({
          url: `/api/v1/auth/getByUsername/${username}`,
          method: 'GET',
        }),
        transformResponse: (response: {
          user: { cncModel: CNCModelI[] } & IUser;
        }) => response.user,
      }
    ),
    addMachine: builder.mutation<
      AuthResponse,
      {
        cncVariantIdList: string[];
      }
    >({
      query: ({ cncVariantIdList }) => ({
        url: '/api/v1/auth/addMachines',
        method: 'POST',
        body: { cncVariantIdList },
      }),
    }),
    deleteMachine: builder.mutation<AuthResponse, string>({
      query: (cncVariantId) => ({
        url: `/api/v1/auth/removeMachine`,
        method: 'POST',
        body: { cncVariantId },
      }),
    }),
  }),
});

apiSlice.enhanceEndpoints({
  addTagTypes: ['User'],
  endpoints: {
    getUser: {
      providesTags: ['User'],
    },
    getUserByUsername: {
      providesTags: ['User'],
    },
    addMachine: {
      invalidatesTags: ['User'],
    },
    deleteMachine: {
      invalidatesTags: ['User'],
    },
    updateUserData: {
      invalidatesTags: ['User'],
    },
  },
});

export const {
  useGetUserQuery,
  useGetSignupSessionQuery,
  useLoginMutation,
  useLogoutMutation,
  useSignupMutation,
  useLazyGetUserQuery,
  useResetPasswordMutation,
  useUpdateUserDataMutation,
  useChangePasswordMutation,
  useUpdateUserPasswordMutation,
  useCompleteUserSignupMutation,
  useLazyGetVerificationCodeQuery,
  useLazyGetGoogleSignInLinkQuery,
  useSendForgetPasswordEmailMutation,
  useValidateVerificationCodeMutation,
  useLazyCheckUsernameAvailabilityQuery,
  useGetUserByUsernameQuery,
  useAddMachineMutation,
  useDeleteMachineMutation,
} = authApiSlice;
